import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { BulkUpdateRequest, Collection, Member, Photo, PhotoRequest, Visibility } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { useCollections } from "../hooks/useCollections";
import { accession, formatDate } from "../lib/format";
import { AdminTable, type Column } from "./AdminTable";
import { AdminForm, type FieldDef, type FormValues } from "./AdminForm";
import type { AdminOutletContext } from "./AdminLayout";
import {
  isoToLocalInput,
  localInputToIso,
  parseTags,
  tagsToInput,
  titleFromFilename,
  toNullableNumber,
  toNullableString,
  toStringList,
} from "./formUtils";
import {
  accessFields,
  BulkDialog,
  mediaBulkActions,
  mediaFilters,
  Thumb,
  useMembers,
  VisibilityCell,
  type OpenBulkDialog,
} from "./mediaAdmin";
import { MediaImport } from "./MediaImport";
import { uploadPhotoFile } from "./mediaTools";

/** Field list; the collection and member options come from the API, so new ones show up here too. */
function fields(collections: Collection[], members: Member[]): FieldDef[] {
  return [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea" },
    {
      name: "collection",
      label: "Collection",
      type: "select",
      options: collections.map((c) => ({ value: c.id, label: c.label })),
      help: "Which shelf this sits on. A sectioned collection stays out of the main gallery.",
    },
    { name: "url", label: "Image", type: "upload", required: true, accept: "image/*",
      help: "Paste an image URL, or upload a file — an upload also makes the thumbnail and fills in the size." },
    { name: "thumbnailUrl", label: "Thumbnail", type: "upload", accept: "image/*" },
    { name: "takenAt", label: "Taken at", type: "datetime" },
    { name: "width", label: "Width (px)", type: "number" },
    { name: "height", label: "Height (px)", type: "number" },
    { name: "tags", label: "Tags", type: "tags", help: "Comma-separated, e.g. scuba, palau" },
    ...accessFields(members),
    {
      name: "featured",
      label: "On the front page",
      type: "checkbox",
      help: "The home page rotates through the featured photos. Order them on the Front page tab.",
    },
  ];
}

function toForm(p?: Photo): FormValues {
  return {
    title: p?.title ?? "",
    description: p?.description ?? "",
    url: p?.url ?? "",
    thumbnailUrl: p?.thumbnailUrl ?? "",
    takenAt: isoToLocalInput(p?.takenAt),
    width: p?.width ?? null,
    height: p?.height ?? null,
    collection: p?.collection ?? "life",
    tags: tagsToInput(p?.tags),
    visibility: p?.visibility ?? "PUBLIC",
    sharedWith: p?.sharedWith ?? [],
    featured: p?.featured ?? false,
  };
}

function toRequest(v: FormValues): PhotoRequest {
  return {
    title: String(v.title ?? "").trim(),
    description: toNullableString(v.description),
    url: String(v.url ?? "").trim(),
    thumbnailUrl: toNullableString(v.thumbnailUrl),
    takenAt: localInputToIso(v.takenAt as string),
    width: toNullableNumber(v.width),
    height: toNullableNumber(v.height),
    collection: toNullableString(v.collection),
    tags: parseTags(v.tags),
    visibility: (v.visibility as Visibility) || "PUBLIC",
    sharedWith: toStringList(v.sharedWith),
    featured: Boolean(v.featured),
  };
}

/** Uploading the main image also uploads a thumbnail and fills in the size (and a title, if blank). */
async function upload(file: File, field: FieldDef) {
  if (field.name !== "url") return (await adminApi.uploadFile(file)).url;
  const up = await uploadPhotoFile(file);
  return {
    url: up.url,
    fill: { thumbnailUrl: up.thumbnailUrl ?? "", width: up.width, height: up.height },
    fillIfEmpty: { title: titleFromFilename(file.name) },
  };
}

export function AdminPhotos() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data, loading, error } = useAsync(() => adminApi.listPhotos(), [refreshToken, reload]);
  const { collections } = useCollections();
  const members = useMembers(refreshToken);
  const FIELDS = fields(collections, members);
  const rows = data?.items ?? [];
  const [editing, setEditing] = useState<Photo | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dialog, setDialog] = useState<OpenBulkDialog | null>(null);
  const refetch = () => setReload((n) => n + 1);

  async function apply(change: Omit<BulkUpdateRequest, "ids">, ids: number[]) {
    await adminApi.bulkPhotos({ ids, ...change });
    refetch();
  }

  const columns: Column<Photo>[] = [
    { key: "thumb", header: "", render: (p) => <Thumb url={p.thumbnailUrl ?? p.url} /> },
    {
      key: "code",
      header: "Code",
      render: (p) => <span className="mono">{accession("PHOTO", p.id)}</span>,
      sortValue: (p) => p.id,
    },
    {
      key: "title",
      header: "Title",
      className: "cell-title",
      render: (p) => p.title,
      sortValue: (p) => p.title,
    },
    {
      key: "collection",
      header: "Collection",
      render: (p) => <span className="cell-muted">{p.collection}</span>,
      sortValue: (p) => p.collection,
    },
    {
      key: "tags",
      header: "Tags",
      render: (p) => <span className="cell-muted">{p.tags.join(", ") || "—"}</span>,
    },
    {
      key: "visibility",
      header: "Visible to",
      render: (p) => <VisibilityCell row={p} />,
      sortValue: (p) => p.visibility,
    },
    {
      key: "featured",
      header: "Front page",
      render: (p) => (
        <span className="cell-muted">{p.featured ? (p.featuredRank ? `#${p.featuredRank}` : "yes") : "—"}</span>
      ),
      sortValue: (p) => (p.featured ? (p.featuredRank ?? 9999) : 99999),
    },
    {
      key: "takenAt",
      header: "Taken",
      render: (p) => <span className="mono">{formatDate(p.takenAt) || "—"}</span>,
      sortValue: (p) => p.takenAt ?? "",
    },
  ];

  return (
    <>
      <AdminTable
        rows={rows}
        getId={(p) => p.id}
        columns={columns}
        searchText={(p) =>
          `${p.title} ${p.collection} ${p.tags.join(" ")} ${p.description ?? ""} ${p.sharedWith.join(" ")}`
        }
        searchPlaceholder="Filter photos…"
        filters={mediaFilters<Photo>(collections)}
        loading={loading}
        error={error ?? undefined}
        emptyLabel="No photos yet."
        createLabel="+ New photo"
        toolbarExtra={
          <button className="btn btn--ghost" onClick={() => setImporting(true)}>
            ⇪ Upload photos…
          </button>
        }
        viewHref={(p) => `/documents/PHOTO-${p.id}`}
        onCreate={() => setCreating(true)}
        onEdit={(p) => setEditing(p)}
        onDelete={async (p) => {
          await adminApi.deletePhoto(p.id);
          refetch();
        }}
        bulkActions={mediaBulkActions<Photo>({
          noun: "photos",
          apply,
          openDialog: setDialog,
          remove: async (sel) => {
            for (const p of sel) await adminApi.deletePhoto(p.id);
            refetch();
          },
        })}
      />
      {creating && (
        <AdminForm
          title="New photo"
          fields={FIELDS}
          initial={toForm()}
          submitLabel="Create"
          onUpload={upload}
          onCancel={() => setCreating(false)}
          onSubmit={async (v) => {
            await adminApi.createPhoto(toRequest(v));
            setCreating(false);
            refetch();
          }}
        />
      )}
      {editing && (
        <AdminForm
          title={`Edit ${accession("PHOTO", editing.id)}`}
          fields={FIELDS}
          initial={toForm(editing)}
          onUpload={upload}
          onCancel={() => setEditing(null)}
          onSubmit={async (v) => {
            await adminApi.updatePhoto(editing.id, toRequest(v));
            setEditing(null);
            refetch();
          }}
        />
      )}
      {importing && (
        <MediaImport
          kind="photo"
          collections={collections}
          members={members}
          onClose={(changed) => {
            setImporting(false);
            if (changed) refetch();
          }}
        />
      )}
      {dialog && (
        <BulkDialog
          dialog={dialog}
          noun="photos"
          members={members}
          collections={collections}
          onCancel={() => setDialog(null)}
          onApply={async (change) => {
            await apply(change, dialog.ids);
            setDialog(null);
          }}
        />
      )}
    </>
  );
}
